/** /org/admin → Herramientas (T5): catálogo editable por el roster + alta masiva por CSV. */
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Plus, Upload, Pencil, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { TagInput } from '@/components/ui/tag-input';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { InternalTable, EmptyState, LoaderBlock } from '@/components/internal/shared';
import { EnumBadge } from '@/components/ui/enum-badge';
import { toastError } from '@/lib/toast-error';
import { useOrgCatalogMutations, useOrgTools } from '@/hooks/useOrg';
import { CsvImportDialog } from '@/components/org/CsvImportDialog';
import { ORG_TOOL_MODALITIES, type OrgToolModality } from '../../../../shared/types/org';
import { toolSlugFromName } from '../../../../shared/org-policy';
import { csvBool, csvNumber, splitCsvList } from '../../../../shared/csv';
import { foldKey } from '../../../../shared/text-normalize';
import type { RouterOutputs } from '@/lib/api-types';

type Tool = RouterOutputs['org']['tools']['list'][number];
/** Forma explícita (el input del server usa z.preprocess y su tipo inferido es `unknown` por campo). */
interface ToolInput {
  name: string;
  slug: string | null;
  category: string | null;
  link: string | null;
  owner_area: string | null;
  owner_email: string | null;
  modality: OrgToolModality | null;
  roles: string[];
  per_client: boolean;
  seats_total: number | null;
  monthly_cost: number | null;
  paid_by: string | null;
}
const NONE = '__none__';

const TOOL_CSV_FIELDS = {
  name: ['herramienta', 'tool', 'nombre'],
  slug: ['slug', 'id'],
  category: ['categoria', 'category', 'rubro'],
  link: ['link', 'url', 'sitio'],
  owner_area: ['area', 'area responsable', 'owner area'],
  owner_email: ['owner', 'responsable', 'dueno', 'owner email'],
  modality: ['modalidad', 'modality'],
  roles: ['roles', 'perfiles'],
  per_client: ['por cliente', 'per client'],
  seats_total: ['asientos', 'seats', 'seats total'],
  monthly_cost: ['costo mensual', 'monthly cost', 'costo'],
  paid_by: ['paga', 'pagado por', 'paid by'],
} as const;
type ToolCsvKey = keyof typeof TOOL_CSV_FIELDS;

const MODALITY_ALIASES: Record<string, OrgToolModality> = { individual: 'individual', compartida: 'shared', shared: 'shared', 'invitacion del cliente': 'client_invite', client_invite: 'client_invite', cliente: 'client_invite', partner: 'partner' };

type Tr = (key: string, fallback: string, opts?: Record<string, unknown>) => string;
const isUrl = (v: string) => { try { new URL(v); return true; } catch { return false; } };
function csvToolRow(r: Partial<Record<ToolCsvKey, string>>, t: Tr): { value: ToolInput } | { error: string } {
  if (!r.name) return { error: t('org.csv.err_name', 'Falta el nombre') };
  const modality = r.modality ? MODALITY_ALIASES[foldKey(r.modality)] : undefined;
  if (r.modality && !modality) return { error: t('org.csv.err_modality', 'Modalidad desconocida: {{value}}', { value: r.modality }) };
  if (r.link && !isUrl(r.link)) return { error: t('org.csv.err_link', 'Link inválido: {{value}}', { value: r.link }) };
  if (r.owner_email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(r.owner_email)) return { error: t('org.csv.err_email', 'Mail inválido: {{value}}', { value: r.owner_email }) };
  const seats = csvNumber(r.seats_total); const cost = csvNumber(r.monthly_cost);
  if (seats !== undefined && !Number.isFinite(seats)) return { error: t('org.csv.err_number', 'Número inválido: {{value}}', { value: r.seats_total }) };
  if (cost !== undefined && !Number.isFinite(cost)) return { error: t('org.csv.err_number', 'Número inválido: {{value}}', { value: r.monthly_cost }) };
  return { value: { name: r.name, slug: r.slug || null, category: r.category || null, link: r.link || null, owner_area: r.owner_area || null, owner_email: r.owner_email || null, modality: modality ?? null, roles: splitCsvList(r.roles), per_client: csvBool(r.per_client) ?? false, seats_total: seats ?? null, monthly_cost: cost ?? null, paid_by: r.paid_by || null } };
}

export function OrgToolsTab() {
  const { t } = useTranslation();
  const q = useOrgTools();
  const m = useOrgCatalogMutations();
  const [editing, setEditing] = useState<Tool | 'new' | null>(null);
  const [removing, setRemoving] = useState<Tool | null>(null);
  const [importing, setImporting] = useState(false);
  const toRow = useCallback((r: Partial<Record<ToolCsvKey, string>>) => csvToolRow(r, t as Tr), [t]);
  if (q.isLoading) return <LoaderBlock />;
  if (q.isError) return <div className="space-y-2"><EmptyState title={t('org.tools.error', 'No se pudo leer el catálogo de herramientas.')} /><Button variant="outline" size="sm" onClick={() => q.refetch()}>{t('common.retry', 'Reintentar')}</Button></div>;
  const tools = q.data ?? [];
  const cols = [
    { key: 'name', label: t('org.tools.col_name', 'Herramienta'), render: (x: Tool) => <span className="font-medium">{x.link ? <a href={x.link} target="_blank" rel="noreferrer" className="hover:underline">{x.name}</a> : x.name}<span className="ml-2 text-xs text-muted-foreground">{x.slug}</span></span> },
    { key: 'category', label: t('org.tools.col_category', 'Categoría'), render: (x: Tool) => x.category ?? '—' },
    { key: 'modality', label: t('org.tools.col_modality', 'Modalidad'), render: (x: Tool) => (x.modality ? <EnumBadge value={x.modality} tone="info" label={t(`org.tools.modality_${x.modality}`, x.modality)} /> : '—') },
    { key: 'per_client', label: t('org.tools.col_per_client', 'Por cliente'), render: (x: Tool) => (x.per_client ? t('common.yes', 'Sí') : t('common.no', 'No')) },
    { key: 'roles', label: t('org.tools.col_roles', 'Roles'), render: (x: Tool) => <span className="text-muted-foreground">{x.roles.join(', ') || '—'}</span> },
    { key: 'owner', label: t('org.tools.col_owner', 'Responsable'), render: (x: Tool) => <span className="text-muted-foreground">{x.owner_email ?? x.owner_area ?? '—'}</span> },
    { key: 'actions', label: '', render: (x: Tool) => (
      <span className="flex gap-1">
        <Button size="sm" variant="ghost" onClick={() => setEditing(x)} aria-label={t('common.edit', 'Editar')}><Pencil className="h-4 w-4" /></Button>
        <Button size="sm" variant="ghost" onClick={() => setRemoving(x)} aria-label={t('common.delete', 'Borrar')}><Trash2 className="h-4 w-4" /></Button>
      </span>
    ) },
  ];
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">{t('org.tools.help', 'Las herramientas que una solución puede exigir por política. El costo es de la herramienta, nunca de una persona.')}</p>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setImporting(true)}><Upload className="mr-1 h-4 w-4" />{t('org.csv.import', 'Importar CSV')}</Button>
          <Button size="sm" onClick={() => setEditing('new')}><Plus className="mr-1 h-4 w-4" />{t('org.tools.new', 'Nueva herramienta')}</Button>
        </div>
      </div>
      {tools.length ? <InternalTable columns={cols} rows={tools} rowKey={(x) => x.slug} /> : <EmptyState title={t('org.tools.empty', 'Todavía no hay herramientas: cargá la primera o importá la lista desde una planilla.')} />}
      {editing && <ToolDialog tool={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />}
      <AlertDialog open={!!removing} onOpenChange={(o) => !o && setRemoving(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('org.tools.remove_title', '¿Borrar {{name}}?', { name: removing?.name ?? '' })}</AlertDialogTitle>
            <AlertDialogDescription>{t('org.tools.remove_body', 'Solo se puede borrar si ninguna política, acceso o licencia la referencia.')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel', 'Cancelar')}</AlertDialogCancel>
            <AlertDialogAction disabled={m.busy} onClick={(e) => { e.preventDefault(); if (!removing) return; m.toolRemove.mutate({ slug: removing.slug }, { onSuccess: () => { toast.success(t('org.tools.removed', 'Herramienta borrada')); setRemoving(null); }, onError: (err) => toastError(err, 'org.tools.error') }); }}>{t('common.delete', 'Borrar')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <CsvImportDialog<ToolCsvKey, ToolInput>
        open={importing}
        onOpenChange={setImporting}
        title={t('org.tools.import_title', 'Importar herramientas desde CSV')}
        description={t('org.tools.import_body', 'Pegá la tabla (una herramienta por fila). Columnas: herramienta, categoría, link, área, responsable, modalidad (individual / compartida / invitación del cliente / partner), roles (separados por ;), por cliente (sí/no), asientos, costo mensual, paga.')}
        fields={TOOL_CSV_FIELDS}
        required={['name']}
        templateHeader="herramienta;categoría;link;área;responsable;modalidad;roles;por cliente;asientos;costo mensual;paga"
        toRow={toRow}
        onImport={async (rows) => { try { return await m.toolImport.mutateAsync({ rows }); } catch (err) { toastError(err, 'org.tools.error'); throw err; } }}
        pending={m.busy}
      />
    </div>
  );
}

function ToolDialog({ tool, onClose }: { tool: Tool | null; onClose: () => void }) {
  const { t } = useTranslation();
  const m = useOrgCatalogMutations();
  const [v, setV] = useState<ToolInput>(tool
    ? { name: tool.name, slug: tool.slug, category: tool.category, link: tool.link, owner_area: tool.owner_area, owner_email: tool.owner_email, modality: tool.modality, roles: tool.roles, per_client: tool.per_client, seats_total: tool.seats_total, monthly_cost: tool.monthly_cost, paid_by: tool.paid_by }
    : { name: '', slug: null, category: null, link: null, owner_area: null, owner_email: null, modality: null, roles: [], per_client: false, seats_total: null, monthly_cost: null, paid_by: null });
  const set = <K extends keyof ToolInput>(k: K, val: ToolInput[K]) => setV((p) => ({ ...p, [k]: val }));
  const text = (k: 'category' | 'link' | 'owner_area' | 'owner_email' | 'paid_by') => (
    <div className="space-y-1">
      <Label htmlFor={`tool-${k}`}>{t(`org.tools.f_${k}`, k)}</Label>
      <Input id={`tool-${k}`} value={v[k] ?? ''} onChange={(e) => set(k, e.target.value || null)} />
    </div>
  );
  const num = (k: 'seats_total' | 'monthly_cost') => (
    <div className="space-y-1">
      <Label htmlFor={`tool-${k}`}>{t(`org.tools.f_${k}`, k)}</Label>
      <Input id={`tool-${k}`} type="number" min={0} step={k === 'monthly_cost' ? '0.01' : '1'} value={v[k] ?? ''} onChange={(e) => set(k, e.target.value === '' ? null : Number(e.target.value))} />
    </div>
  );
  const save = () => {
    if (!v.name.trim()) return;
    m.toolUpsert.mutate({ ...v, slug: tool ? tool.slug : v.slug || toolSlugFromName(v.name) }, { onSuccess: () => { toast.success(t('org.tools.saved', 'Herramienta guardada')); onClose(); }, onError: (err) => toastError(err, 'org.tools.error') });
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{tool ? t('org.tools.edit_title', 'Editar herramienta') : t('org.tools.new', 'Nueva herramienta')}</DialogTitle>
          <DialogDescription>{t('org.tools.dialog_body', 'El slug se arma solo desde el nombre y es la clave que usan las políticas y la evidencia del Vault (tag tool:<slug>).')}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1 sm:col-span-2">
            <Label htmlFor="tool-name">{t('org.tools.f_name', 'Nombre')}</Label>
            <Input id="tool-name" value={v.name} onChange={(e) => set('name', e.target.value)} />
            <p className="text-xs text-muted-foreground">{t('org.tools.f_slug', 'Slug')}: {tool ? tool.slug : toolSlugFromName(v.name) || '—'}</p>
          </div>
          {text('category')}
          {text('link')}
          {text('owner_area')}
          {text('owner_email')}
          <div className="space-y-1">
            <Label htmlFor="tool-modality">{t('org.tools.f_modality', 'Modalidad')}</Label>
            <Select value={v.modality ?? NONE} onValueChange={(val) => set('modality', val === NONE ? null : (val as OrgToolModality))}>
              <SelectTrigger id="tool-modality"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>—</SelectItem>
                {ORG_TOOL_MODALITIES.map((mo) => <SelectItem key={mo} value={mo}>{t(`org.tools.modality_${mo}`, mo)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2 pt-6">
            <Checkbox id="tool-per-client" checked={!!v.per_client} onCheckedChange={(c) => set('per_client', c === true)} />
            <Label htmlFor="tool-per-client">{t('org.tools.f_per_client', 'El acceso es por cliente (una cuenta por cliente)')}</Label>
          </div>
          <div className="space-y-1 sm:col-span-2">
            <Label htmlFor="tool-roles-input">{t('org.tools.f_roles', 'Roles que existen en la herramienta')}</Label>
            <TagInput listId="tool-roles" value={v.roles ?? []} onChange={(r) => set('roles', r)} placeholder={t('org.tools.roles_ph', 'admin, editor, viewer…')} />
          </div>
          {num('seats_total')}
          {num('monthly_cost')}
          {text('paid_by')}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t('common.cancel', 'Cancelar')}</Button>
          <Button onClick={save} disabled={m.busy || !v.name.trim()}>{t('common.save', 'Guardar')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
